'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem } from '@heidi/design-system/page-shell';
import { MoreHorizontal } from 'lucide-react';
import { useCallback, useTransition } from 'react';
import { useIntl } from 'react-intl';

import { setSidebarOpenTab } from '@/atoms/persistedPreferencesAtoms';
import { PatientListNavSpotlight } from '@/components/layout/Navigation/PatientListNavSpotlight';
import { useMobilePanel } from '@/components/layout/Navigation/mobile';
import { type FooterMoreRowDescriptor } from '@/components/layout/Navigation/sidebar/footer-rail/footer-rail-context';
import { FooterRailMoreRow } from '@/components/layout/Navigation/sidebar/footer-rail/footer-rail-more-row';
import { usePrimaryNavRail } from '@/components/layout/Navigation/sidebar/primary-nav-rail/primary-nav-rail-context';
import { PRIMARY_NAV_MORE_TEST_ID } from '@/components/layout/Navigation/sidebar/primary-nav-rail/primary-nav-rail.constants';
import { useDictationSidebarSpotlightPorts } from '@/composition/hosts/dictation/spotlights/dictation-sidebar-spotlight-ports';
import { useIsPatientListEnabled } from '@/features/flags';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { DictateScribeCrossSellSpotlight, DictationLogsSpotlight } from '@heidi/dictate';
import { useRouter } from 'next/router';

interface PrimaryNavMoreGroupProps {
  /**
   * Overflow items always shown in More regardless of screen height
   * (Patients, Templates, Translator, etc.).
   */
  overflowDescriptors: readonly FooterMoreRowDescriptor[];
}

/**
 * Sidebar IA primary-nav More group (MUL-3702).
 *
 * Always visible. Combines:
 * - `moreDescriptors` from context: fixed items (Home/Scribe/Library/Tasks)
 *   that have collapsed due to limited screen height — prepended so they
 *   appear above the overflow items.
 * - `overflowDescriptors`: items always in More (Patients, Templates, etc.)
 */
export function PrimaryNavMoreGroup({ overflowDescriptors }: PrimaryNavMoreGroupProps) {
  const intl = useIntl();
  const {
    moreDescriptors: collapsedFixed,
    isMoreActive,
    containerRef,
  } = usePrimaryNavRail();
  const isPatientsEnabled = useIsPatientListEnabled();
  const isEhrLaunch = useIsEhrLaunch();
  const router = useRouter();
  const [, startTransition] = useTransition();
  const { dismissMobileSidebar } = useMobilePanel();
  const { isDictationLogsEnabled, isOnDictationLogsRoute, onStartScribeSession } =
    useDictationSidebarSpotlightPorts();

  const label = intl.formatMessage({
    defaultMessage: 'More',
    id: 'mwxWa9JeGQ',
    description: 'Label for the primary nav More dropdown trigger',
  });

  const allMoreDescriptors = [...collapsedFixed, ...overflowDescriptors];

  const handlePatientsShowMe = useCallback(() => {
    void router.push('/patients');
    startTransition(() => setSidebarOpenTab(undefined));
    dismissMobileSidebar();
  }, [router, startTransition, dismissMobileSidebar]);

  return (
    <PatientListNavSpotlight
      onShowMe={isPatientsEnabled && !isEhrLaunch ? handlePatientsShowMe : undefined}
    >
      {/* The footer Dictate button hosts it until Dictate folds into More. */}
      <DictationLogsSpotlight
        enabled={allMoreDescriptors.some((descriptor) => descriptor.id === 'dictate')}
        isDictationLogsEnabled={isDictationLogsEnabled}
      >
        <DictateScribeCrossSellSpotlight
          isOnDictationLogsRoute={isOnDictationLogsRoute}
          onStartScribeSession={onStartScribeSession}
        >
          <SidebarMenuItem>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  ref={containerRef}
                  isActive={isMoreActive || overflowDescriptors.some((d) => d.isActive)}
                  tooltip={label}
                  aria-label={label}
                  data-testid={PRIMARY_NAV_MORE_TEST_ID}
                >
                  <MoreHorizontal />
                  <span>{label}</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              {/* Offsets target the 36px pill, not the full-width tile: it's inset
                  14px per side and 4px from the top, leaving a 12px gap. */}
              <DropdownMenuContent
                side="right"
                align="start"
                sideOffset={-2}
                alignOffset={4}
                collisionPadding={{ top: 8, bottom: 8 }}
                onCloseAutoFocus={(event) => event.preventDefault()}
                className="min-w-52 rounded-lg"
              >
                {allMoreDescriptors.map((descriptor) => (
                  <FooterRailMoreRow key={descriptor.id} descriptor={descriptor} />
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </DictateScribeCrossSellSpotlight>
      </DictationLogsSpotlight>
    </PatientListNavSpotlight>
  );
}
